import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdClose, 
  MdArrowBack, 
  MdChevronRight,
  MdMoreVert,
  MdAssignment,
  MdLink,
  MdLinkOff,
  MdFileUpload,
  MdFileDownload,
  MdAttachFile,
  MdDescription
} from 'react-icons/md';
import { CiMenuKebab } from "react-icons/ci";

// Contract tipleri için sabit değerler
const CONTRACT_TYPES = [
  { value: 'Support', label: 'Support' },
  { value: 'Project', label: 'Project' },
  { value: 'Managed Services', label: 'Managed Services' }
];

const STATUS_OPTIONS = [
  { value: 1, label: 'Active' },
  { value: 0, label: 'Deactive' }
];

const SlaStatusBadge = ({ hasAssignedSla, slaName, onUnassign }) => (
  <div className="flex items-center gap-2">
    <span className={`px-3 py-1.5 text-xs font-medium rounded-full flex items-center gap-2 ${
      hasAssignedSla 
        ? 'bg-green-100 text-green-800'
        : 'bg-gray-100 text-gray-800'
    }`}>
      {hasAssignedSla ? (
        <>
          <span>Assigned to: {slaName}</span>
          <button
            onClick={() => onUnassign()}
            className="ml-2 p-0.5 hover:bg-green-200 rounded-full transition-colors"
            title="Unassign SLA"
          >
            <MdClose size={14} />
          </button>
        </>
      ) : 'Unassigned'}
    </span>
  </div>
);

const ActionButtons = ({ product, onEdit, onDelete, onAssignSla }) => {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onEdit(product)}
        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        title="Edit Product"
      >
        <MdEdit size={18} />
      </button>
      <button
        onClick={() => onDelete(product.id, product.product_name)}
        className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
        title="Delete Product"
      >
        <MdDelete size={18} />
      </button>
      <button
        onClick={() => onAssignSla(product)}
        className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
        title={product.sla_group_name ? "Change SLA" : "Assign SLA"}
      >
        {product.sla_group_name ? (
          <MdLink size={18} />
        ) : (
          <MdAssignment size={18} />
        )}
      </button>
    </div>
  );
};

const SlaDetails = ({ product, onUnassign }) => {
  if (!product.sla_group_name) return null;

  return (
    <div className="ml-8 border-l-4 border-green-400 pl-4 py-2 flex items-center justify-between bg-green-50 rounded-r-md">
      <div className="flex items-center space-x-3">
        <MdLink className="text-green-600" size={18} />
        <div>
          <span className="text-sm font-medium text-green-800">Assigned SLA:</span>
          <span className="text-sm text-green-700 ml-2">{product.sla_group_name}</span>
        </div>
      </div>
      <button
        onClick={() => onUnassign(product)}
        className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1 px-3 py-1 hover:bg-red-50 rounded-md transition-colors"
      >
        <MdLinkOff size={16} />
        Unassign
      </button>
    </div>
  );
};

const ContractDetail = () => {
  const { t } = useLanguage();
  const { id } = useParams(); // Bu artık UUID olacak
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    contrat_id: null, // Bu gerçek contract ID ile doldurulacak
    customer_id: null,
    sla_group_id: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });

  // File upload related states
  const [files, setFiles] = useState([]);
  const [contractFiles, setContractFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileDescription, setFileDescription] = useState('');
  const [showFileUploadForm, setShowFileUploadForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // SLA için state'ler
  const [showSlaAssignForm, setShowSlaAssignForm] = useState(false);
  const [selectedProductForSla, setSelectedProductForSla] = useState(null);
  const [availableSlas, setAvailableSlas] = useState([]);
  const [selectedSlaId, setSelectedSlaId] = useState('');

  // Contract edit için state'ler
  const [showContractEditForm, setShowContractEditForm] = useState(false);
  const [contractFormData, setContractFormData] = useState({
    contract_status: 1,
    contract_id: '',
    contract_name: '',
    contract_description: '',
    contract_type: '',
    ticket_count: '',
    company_account_manager: '',
    primary_technical_contact: '',
    start_date: '',
    end_date: '',
    contract_scope: '',
    customer_id: ''
  });
  const [isContractEditing, setIsContractEditing] = useState(false);
  const [contractEditingId, setContractEditingId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showContractActionsMenu, setShowContractActionsMenu] = useState(false);
  const [showFilesActionsMenu, setShowFilesActionsMenu] = useState(false);
  const [showProductsActionsMenu, setShowProductsActionsMenu] = useState(false);





  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // UUID ile contract detayını al
      const contractResponse = await api.get(`/contracts/${id}`);
      if (contractResponse.data.success) {
        const contractData = contractResponse.data.contract;
        setContract(contractData);
        
        // UUID'den gerçek contract ID'sini al ve formData'yı güncelle
        const realContractId = contractData.id;
        setFormData(prev => ({
          ...prev,
          contrat_id: realContractId,
          customer_id: contractData.customer_id
        }));

        // Diğer API çağrılarında gerçek contract ID'yi kullan
        const productsResponse = await api.get(`/products/${realContractId}`);
        if (productsResponse.data.success) {
          setProducts(productsResponse.data.products);
        }

        // Fetch contract files
        const filesResponse = await api.get(`/contracts/${realContractId}/files`);
        if (filesResponse.data.success) {
          setContractFiles(filesResponse.data.files);
        }
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      showPopup({
        title: t('common.error'),
        message: t('contractDetail.errorLoadingData'),
        type: 'error',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId, productName) => {
    showPopup({
      title: t('contractDetail.deleteProduct'),
      message: t('contractDetail.confirmDeleteProduct', { productName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/products/${productId}`);
          if (response.data.success) {
            setProducts(products.filter(product => product.id !== productId));
            showPopup({
              title: t('common.success'),
              message: t('contractDetail.productDeletedSuccess', { productName }),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('contractDetail.errorDeletingProduct'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleEdit = (product) => {
    setFormData({
      product_name: product.product_name,
      contrat_id: contract.id, // Gerçek contract ID'sini kullan
      customer_id: contract.customer_id,
      sla_group_id: product.sla_group_id
    });
    setIsEditing(true);
    setEditingId(product.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (isEditing) {
        response = await api.put(`/products/${editingId}`, formData);
      } else {
        response = await api.post('/products', formData);
      }

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: isEditing ? t('contractDetail.productUpdatedSuccess') : t('contractDetail.productAddedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            setShowForm(false);
            setIsEditing(false);
            setEditingId(null);
            fetchData();
            closePopup();
          }
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('contractDetail.errorDuringOperation'),
        type: 'error',
        showCancel: false
      });
    }
  };

  // Contract edit form submit fonksiyonu
  const handleContractSubmit = async (e) => {
    e.preventDefault();
    try {
      const userStr = localStorage.getItem('user');
      const userData = JSON.parse(userStr);

      // Form verilerini hazırla
      const submitData = {
        ...contractFormData,
        company_id: userData.companyId,
        // Eğer ticket_count "unlimited" ise 0 olarak gönder
        ticket_count: contractFormData.ticket_count === 'unlimited' ? 0 : contractFormData.ticket_count
      };

      const response = await api.put(`/contracts/${contractEditingId}`, submitData);

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: t('contractDetail.contractUpdatedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            setShowContractEditForm(false);
            setIsContractEditing(false);
            setContractEditingId(null);
            fetchData(); // Contract verilerini yenile
            closePopup();
          }
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        showPopup({
          title: t('common.error'),
          message: error.response?.data?.message || t('contractDetail.errorUpdatingContract'),
          type: 'error',
          showCancel: false
        });
      }
    }
  };

  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || '',
      message: options.message || '',
      type: options.type || 'confirm',
      onConfirm: options.onConfirm || null,
      showCancel: options.showCancel !== undefined ? options.showCancel : true
    });
  };

  const closePopup = () => {
    setPopup({
      show: false,
      title: '',
      message: '',
      type: 'confirm',
      onConfirm: null,
      showCancel: true
    });
  };

  // Contract form change handler
  const handleContractChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContractFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Contract edit fonksiyonu - Edit popup'ını aç
  const handleContractEdit = () => {
    // Contract verilerini contractFormData'ya yükle
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('Tarih formatlanırken hata:', error);
        return '';
      }
    };

    setContractFormData({
      contract_status: contract.contract_status === 1,
      contract_id: contract.contract_id || '',
      contract_name: contract.contract_name || '',
      contract_description: contract.contract_description || '',
      contract_type: contract.contract_type || '',
      ticket_count: contract.ticket_count === 0 ? 'unlimited' : (contract.ticket_count || ''),
      company_account_manager: contract.company_account_manager || '',
      primary_technical_contact: contract.primary_technical_contact || '',
      start_date: formatDate(contract.start_date),
      end_date: formatDate(contract.end_date),
      contract_scope: contract.contract_scope || '',
      customer_id: contract.customer_id || ''
    });
    setIsContractEditing(true);
    setContractEditingId(contract.id);
    setShowContractEditForm(true);
    
    // Müşterileri getir
    fetchCustomers();
  };



  // Contract delete fonksiyonu - ContractSettings'teki handleDelete fonksiyonunu kullan
  const handleContractDelete = () => {
    showPopup({
      title: t('contractDetail.deleteContract'),
      message: t('contractDetail.confirmDeleteContract', { contractName: contract.contract_name }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/contracts/${contract.id}`);
          if (response.data.success) {
            showPopup({
              title: t('common.success'),
              message: t('contractDetail.contractDeletedSuccess', { contractName: contract.contract_name }),
              type: 'success',
              showCancel: false,
              onConfirm: () => {
                closePopup();
                navigate('/settings/contract');
              }
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('contractDetail.errorDeletingContract'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };



  // Ürün listesi tablosunda her ürün için SLA atama butonu
  const handleAssignSla = (product) => {
    setSelectedProductForSla(product);
    setSelectedSlaId('');
    setShowSlaAssignForm(true);
    fetchSlas();
  };

  // SLA atama işlemi
  const handleSlaAssignment = async () => {
    try {
      const response = await api.put(`/products/${selectedProductForSla.id}/assign-sla`, {
        sla_id: selectedSlaId
      });

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: t('contractDetail.slaAssignedSuccess'),
          type: 'success',
          showCancel: false
        });
        setShowSlaAssignForm(false);
        fetchData(); // Ürün listesini yenile
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('contractDetail.errorAssigningSla'),
        type: 'error',
        showCancel: false
      });
    }
  };

  // SLA'ları getir
  const fetchSlas = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const response = await api.get(`/sla/all/${userData.companyId}`);
        if (response.data.success) {
          setAvailableSlas(response.data.slas);
        }
      }
    } catch (error) {
      console.error('SLA listesi alınırken hata:', error);
    }
  };

  // Müşterileri getir
  const fetchCustomers = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const response = await api.get(`/customers/${userData.companyId}`);
        if (response.data.success) {
          setCustomers(response.data.customers);
        }
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
    }
  };

  useEffect(() => {
    fetchSlas();
  }, []);

  // SLA atamasını kaldır
  const handleUnassignSla = async (product) => {
    try {
      const response = await api.put(
        `/products/${product.id}/assign-sla`,
        { sla_id: null }
      );

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: t('contractDetail.slaUnassignedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            fetchData();
            closePopup();
          }
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('contractDetail.errorUnassigningSla'),
        type: 'error',
        showCancel: false
      });
    }
  };

  // File upload handlers
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prevFiles => [...prevFiles, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleFileUpload = async () => {
    if (files.length === 0) {
      showPopup({
        title: t('common.warning'),
        message: t('contractDetail.pleaseSelectFile'),
        type: 'error',
        showCancel: false
      });
      return;
    }

    if (!fileDescription.trim()) {
      showPopup({
        title: t('common.warning'),
        message: t('contractDetail.pleaseEnterFileDescription'),
        type: 'error',
        showCancel: false
      });
      return;
    }

    try {
      setIsUploading(true);
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('contract_id', id);
      formData.append('file_description', fileDescription);
      
      // Get user info from localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        formData.append('uploaded_by', userData.id);
      }

      const response = await api.post(
        '/contracts/upload-files',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data.success) {
        setFiles([]);
        setFileDescription('');
        setShowFileUploadForm(false);
        fetchData(); // Refresh file list
        showPopup({
          title: t('common.success'),
          message: t('contractDetail.filesUploadedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            closePopup();
          }
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('contractDetail.errorUploadingFiles'),
        type: 'error',
        showCancel: false
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    showPopup({
      title: t('contractDetail.deleteFile'),
      message: t('contractDetail.confirmDeleteFile', { fileName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/contracts/files/${fileId}`);
          if (response.data.success) {
            setContractFiles(contractFiles.filter(file => file.id !== fileId));
            showPopup({
              title: t('common.success'),
              message: t('contractDetail.fileDeletedSuccess', { fileName }),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('contractDetail.errorDeletingFile'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await api.get(`/contracts/files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: t('contractDetail.errorDownloadingFile'),
        type: 'error',
        showCancel: false
      });
    }
  };

  // Get file icon based on file extension
  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    
    switch(extension) {
      case 'pdf':
        return <span className="text-red-600"><MdDescription size={24} /></span>;
      case 'doc':
      case 'docx':
        return <span className="text-blue-600"><MdDescription size={24} /></span>;
      case 'xls':
      case 'xlsx':
        return <span className="text-green-600"><MdDescription size={24} /></span>;
      case 'ppt':
      case 'pptx':
        return <span className="text-orange-600"><MdDescription size={24} /></span>;
      default:
        return <span className="text-gray-600"><MdAttachFile size={24} /></span>;
    }
  };

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  if (!contract) {
    return <div>{t('contractDetail.contractNotFound')}</div>;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm mb-6 pt-4">
        <div className="flex items-center bg-[#232b3b] dark:bg-gray-700 px-4 py-2 rounded-md shadow-sm">
          <Link 
            to="/settings/contract" 
            className="text-[#fff] dark:text-white hover:text-[gray] dark:hover:text-gray-300 font-medium transition-colors duration-200"
          >
            {t('common.settings')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff] dark:text-white" size={20} />
          <Link 
            to="/settings/contract" 
            className="text-[#fff] dark:text-white hover:text-[gray] dark:hover:text-gray-300 font-medium transition-colors duration-200"
          >
            {t('contractDetail.contracts')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff] dark:text-white" size={20} />
          <span className="text-[#fff] dark:text-white font-medium">
            {contract.contract_name}
          </span>
        </div>
      </nav>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 gap-6 pb-6">
          {/* Contract Details Tablosu */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
              <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('contractDetail.contractDetails')}</h1>
              <div className="relative">
                {/* Actions Menu Button */}
                <button
                  onClick={() => setShowContractActionsMenu(!showContractActionsMenu)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                  title="Actions"
                >
                  <CiMenuKebab size={20} />
                </button>
                
                {/* Actions Dropdown Menu */}
                {showContractActionsMenu && (
                  <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleContractEdit();
                          setShowContractActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdEdit size={16} />
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => {
                          handleContractDelete();
                          setShowContractActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdDelete size={16} />
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                   <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.contractName')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.contract_name}</td>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.customer')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.customer_name}</td>
                     </tr>
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.contractType')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.contract_type}</td>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.status')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">
                         <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                           contract.contract_status ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                         }`}>
                           {contract.contract_status ? t('contractDetail.active') : t('contractDetail.inactive')}
                         </span>
                       </td>
                     </tr>
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.startDate')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{new Date(contract.start_date).toLocaleDateString()}</td>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.endDate')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{new Date(contract.end_date).toLocaleDateString()}</td>
                     </tr>
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.contractId')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.contract_id}</td>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.ticketCount')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.ticket_count === 0 ? t('contractDetail.unlimited') : contract.ticket_count}</td>
                     </tr>
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.companyAccountManager')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.company_account_manager || t('contractDetail.notAssigned')}</td>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.technicalManager')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white">{contract.primary_technical_contact || t('contractDetail.notAssigned')}</td>
                     </tr>
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.description')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white" colSpan="3">{contract.contract_description}</td>
                     </tr>
                     <tr>
                       <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('contractDetail.contractScope')}</td>
                       <td className="px-4 py-2 text-gray-900 dark:text-white" colSpan="3">{contract.contract_scope}</td>
                     </tr>
                   </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4 p-4">
                {/* Contract Name & Customer */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.contractName')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.contract_name}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.customer')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.customer_name}</div>
                  </div>
                </div>

                {/* Contract Type & Status */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.contractType')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.contract_type}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.status')}</div>
                    <div className="text-gray-900 dark:text-white">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        contract.contract_status ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                      }`}>
                        {contract.contract_status ? t('contractDetail.active') : t('contractDetail.inactive')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.startDate')}</div>
                    <div className="text-gray-900 dark:text-white">{new Date(contract.start_date).toLocaleDateString()}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.endDate')}</div>
                    <div className="text-gray-900 dark:text-white">{new Date(contract.end_date).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Contract ID & Ticket Count */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.contractId')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.contract_id}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.ticketCount')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.ticket_count === 0 ? t('contractDetail.unlimited') : contract.ticket_count}</div>
                  </div>
                </div>

                {/* Company Account Manager & Technical Manager */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.companyAccountManager')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.company_account_manager || t('contractDetail.notAssigned')}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.technicalManager')}</div>
                    <div className="text-gray-900 dark:text-white">{contract.primary_technical_contact || t('contractDetail.notAssigned')}</div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.description')}</div>
                  <div className="text-gray-900 dark:text-white">{contract.contract_description}</div>
                </div>

                {/* Contract Scope */}
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.contractScope')}</div>
                  <div className="text-gray-900 dark:text-white">{contract.contract_scope}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contract Files Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('contractDetail.contractFiles')}</h2>
              <div className="relative">
                <button
                  onClick={() => setShowFilesActionsMenu(!showFilesActionsMenu)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                  title="Actions"
                >
                  <CiMenuKebab size={20} />
                </button>
                {showFilesActionsMenu && (
                  <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => { setShowFileUploadForm(true); setShowFilesActionsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdFileUpload size={16} />
                        {t('contractDetail.uploadFiles')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[5%]">
                          {t('contractDetail.type')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[30%]">
                          {t('contractDetail.fileName')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[30%]">
                          {t('contractDetail.description')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[20%]">
                          {t('contractDetail.uploadDate')}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[15%]">
                          {t('contractDetail.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {contractFiles.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                            {t('contractDetail.noFilesUploadedYet')}
                          </td>
                        </tr>
                      ) : (
                        contractFiles.map((file, index) => {
                          const isLast = index === contractFiles.length - 1;
                          return (
                            <tr key={file.id}>
                              {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                              <td className={`px-4 py-2 ${isLast ? 'rounded-bl-lg' : ''}`}>
                                <div className="flex items-center">
                                  {getFileIcon(file.filename)}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {file.filename}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {file.file_description}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {new Date(file.created_at).toLocaleString()}
                                </div>
                              </td>
                              {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                              <td className={`px-4 py-2 text-right ${isLast ? 'rounded-br-lg' : ''}`}>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => handleDownloadFile(file.id, file.filename)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-full transition-colors"
                                    title="Download File"
                                  >
                                    <MdFileDownload size={18} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFile(file.id, file.filename)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-full transition-colors"
                                    title="Delete File"
                                  >
                                    <MdDelete size={18} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4 p-4">
                {contractFiles.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 dark:text-gray-500 text-sm">{t('contractDetail.noFilesUploadedYet')}</p>
                  </div>
                ) : (
                  contractFiles.map((file, index) => (
                    <div key={file.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                      {/* Type & File Name */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.type')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">
                            <div className="flex items-center">
                              {getFileIcon(file.filename)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.fileName')}</div>
                          <div className="text-sm text-gray-900 dark:text-white font-medium">{file.filename}</div>
                        </div>
                      </div>

                      {/* Description & Upload Date */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.description')}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{file.file_description}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.uploadDate')}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{new Date(file.created_at).toLocaleString()}</div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.actions')}</div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadFile(file.id, file.filename)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-full transition-colors"
                            title="Download File"
                          >
                            <MdFileDownload size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteFile(file.id, file.filename)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-full transition-colors"
                            title="Delete File"
                          >
                            <MdDelete size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('contractDetail.products')}</h2>
              <div className="relative">
                <button
                  onClick={() => setShowProductsActionsMenu(!showProductsActionsMenu)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                  title="Actions"
                >
                  <CiMenuKebab size={20} />
                </button>
                {showProductsActionsMenu && (
                  <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => { setShowForm(true); setShowProductsActionsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdAdd size={16} />
                        {t('contractDetail.addProductToContract')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[45%]">
                        {t('contractDetail.productName')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[45%]">
                        {t('contractDetail.slaStatus')}
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[10%]">
                        {t('contractDetail.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                          {t('contractDetail.noProductsFound')}
                        </td>
                      </tr>
                    ) : (
                      products.map((product, index) => {
                        const isLast = index === products.length - 1;
                        return (
                          <React.Fragment key={product.id}>
                            <tr>
                              {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                              <td className={`px-4 py-2 ${isLast ? 'rounded-bl-lg' : ''}`}>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {product.product_name}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex flex-col gap-2">
                                  <SlaStatusBadge 
                                    hasAssignedSla={!!product.sla_group_name} 
                                    slaName={product.sla_group_name}
                                    onUnassign={() => {
                                      showPopup({
                                        title: 'Unassign SLA',
                                        message: `Are you sure you want to unassign "${product.sla_group_name}" from "${product.product_name}"?`,
                                        type: 'confirm',
                                        onConfirm: () => handleUnassignSla(product)
                                      });
                                    }}
                                  />
                                </div>
                              </td>
                              {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                              <td className={`px-4 py-2 text-right ${isLast ? 'rounded-br-lg' : ''}`}>
                                <div className="flex justify-end">
                                  <ActionButtons
                                    product={product}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onAssignSla={handleAssignSla}
                                  />
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View - 2 Column Layout */}
              <div className="lg:hidden space-y-3 p-4">
                {products.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 dark:text-gray-500 text-sm">{t('contractDetail.noProductsFound')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {products.map((product, index) => (
                      <div key={product.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                        {/* Product Name */}
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.productName')}</div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{product.product_name}</div>
                        </div>

                        {/* SLA Status */}
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.slaStatus')}</div>
                          <div className="text-sm">
                            <SlaStatusBadge 
                              hasAssignedSla={!!product.sla_group_name} 
                              slaName={product.sla_group_name}
                              onUnassign={() => {
                                showPopup({
                                  title: 'Unassign SLA',
                                  message: `Are you sure you want to unassign "${product.sla_group_name}" from "${product.product_name}"?`,
                                  type: 'confirm',
                                  onConfirm: () => handleUnassignSla(product)
                                });
                              }}
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractDetail.actions')}</div>
                          <div className="flex items-center gap-1">
                            <ActionButtons
                              product={product}
                              onEdit={handleEdit}
                              onDelete={handleDelete}
                              onAssignSla={handleAssignSla}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Form Modal - Edit Agent stili */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header - Edit Agent stili */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {isEditing ? t('contractDetail.editProduct') : t('contractDetail.addProductToContract')}
              </h3>
              <button 
                onClick={() => setShowForm(false)}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.productName')}
                  </label>
                  <input
                    type="text"
                    name="product_name"
                    value={formData.product_name}
                    onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* SLA Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.slaGroupOptional')}
                  </label>
                  <select
                    value={formData.sla_group_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, sla_group_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('contractDetail.selectSlaGroup')}</option>
                    {availableSlas.map(sla => (
                      <option key={sla.id} value={sla.id}>
                        {sla.sla_group_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Form Buttons - Edit Agent stili */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {isEditing ? t('contractDetail.updateProduct') : t('contractDetail.addProduct')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Popup Component */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SLA Atama Modal - Edit Agent stili */}
      {showSlaAssignForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
          <div className="relative mx-auto w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header - Edit Agent stili */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {t('contractDetail.assignSlaTo')} {selectedProductForSla?.product_name}
              </h3>
              <button 
                onClick={() => setShowSlaAssignForm(false)}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>

            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.selectSla')}
                  </label>
                  <select
                    value={selectedSlaId}
                    onChange={(e) => setSelectedSlaId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('contractDetail.selectAnSla')}</option>
                    {availableSlas.map(sla => (
                      <option key={sla.id} value={sla.id}>
                        {sla.sla_group_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Form Buttons - Edit Agent stili */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowSlaAssignForm(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSlaAssignment}
                    disabled={!selectedSlaId}
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('contractDetail.assignSla')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal - Edit Agent stili */}
      {showFileUploadForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header - Edit Agent stili */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {t('contractDetail.uploadContractFiles')}
              </h3>
              <button 
                onClick={() => {
                  setShowFileUploadForm(false);
                  setFiles([]);
                  setFileDescription('');
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.fileDescription')}
                  </label>
                  <input
                    type="text"
                    value={fileDescription}
                    onChange={(e) => setFileDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('contractDetail.enterFileDescription')}
                    required
                  />
                </div>

                {/* Drag and drop area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center ${
                    isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                >
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <MdFileUpload className="text-gray-400 dark:text-gray-500" size={48} />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('contractDetail.dragAndDropFiles')}
                    </p>
                    <label className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] cursor-pointer">
                      <span>{t('contractDetail.browseFiles')}</span>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('contractDetail.supportedFileTypes')}
                    </p>
                  </div>
                </div>

                {/* Selected files list */}
                {files.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('contractDetail.selectedFiles')}:</h4>
                    <ul className="max-h-40 overflow-y-auto border dark:border-gray-600 rounded-md divide-y dark:divide-gray-600 bg-white dark:bg-gray-700">
                      {files.map((file, index) => (
                        <li key={index} className="px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {getFileIcon(file.name)}
                            <span className="text-sm truncate max-w-xs text-gray-900 dark:text-white">{file.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({(file.size / 1024).toFixed(2)} KB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-400"
                          >
                            <MdClose size={18} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Upload progress */}
                {isUploading && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-center mt-1 text-gray-600 dark:text-gray-400">
                      Uploading: {uploadProgress}%
                    </p>
                  </div>
                )}

                {/* Form Buttons - Edit Agent stili */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFileUploadForm(false);
                      setFiles([]);
                      setFileDescription('');
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    disabled={isUploading}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleFileUpload}
                    disabled={files.length === 0 || !fileDescription.trim() || isUploading}
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? t('contractDetail.uploading') : t('contractDetail.uploadFiles')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Edit Modal - Edit Agent stili */}
      {showContractEditForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
          <div className="relative mx-auto w-[900px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header - Edit Agent stili */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {t('contractDetail.editContract')}
              </h3>
              <button 
                onClick={() => {
                  setShowContractEditForm(false);
                  setIsContractEditing(false);
                  setContractEditingId(null);
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleContractSubmit} className="grid grid-cols-3 gap-4">
                {/* Status - En başta */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.status')}
                  </label>
                  <select
                    name="contract_status"
                    value={contractFormData.contract_status}
                    onChange={handleContractChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                    required
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contract ID - readonly */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.contractId')}
                  </label>
                  <input
                    type="text"
                    name="contract_id"
                    value={contractFormData.contract_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md bg-gray-100"
                    readOnly
                  />
                </div>

                {/* Contract Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.contractName')}
                  </label>
                  <input
                    type="text"
                    name="contract_name"
                    value={contractFormData.contract_name}
                    onChange={handleContractChange}
                    autoComplete="new-contract-name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                    required
                  />
                </div>

                {/* Customer Selection ve Contract Type yan yana */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.customer')}
                  </label>
                  <select
                    name="customer_id"
                    value={contractFormData.customer_id || ''}
                    onChange={handleContractChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.customer_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contract Type */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.contractType')}
                  </label>
                  <select
                    name="contract_type"
                    value={contractFormData.contract_type}
                    onChange={handleContractChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                    required
                  >
                    <option value="">Select Contract Type</option>
                    {CONTRACT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ticket Count */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.ticketCount')}
                  </label>
                  <select
                    name="ticket_count"
                    value={contractFormData.ticket_count}
                    onChange={handleContractChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                    required
                  >
                    <option value="">Select Ticket Count</option>
                    <option value="unlimited">Unlimited</option>
                    {[10, 20, 30, 40, 50, 100].map(count => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Company Account Manager ve Primary Technical Contact yan yana */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.companyAccountManager')}
                  </label>
                  <input
                    type="text"
                    name="company_account_manager"
                    value={contractFormData.company_account_manager}
                    onChange={handleContractChange}
                    autoComplete="new-account-manager"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.primaryTechnicalContact')}
                  </label>
                  <input
                    type="text"
                    name="primary_technical_contact"
                    value={contractFormData.primary_technical_contact}
                    onChange={handleContractChange}
                    autoComplete="new-technical-contact"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Start Date */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.startDate')}
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={contractFormData.start_date}
                    onChange={handleContractChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Contract Description - Start Date'in yanında, 2 sütun genişliğinde */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.contractDescription')}
                  </label>
                  <input
                    type="text"
                    name="contract_description"
                    value={contractFormData.contract_description}
                    onChange={handleContractChange}
                    autoComplete="new-contract-description"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* End Date - Ayrı satırda */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.endDate')}
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={contractFormData.end_date}
                    onChange={handleContractChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Contract Scope - En altta tam genişlikte */}
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractDetail.contractScope')}
                  </label>
                  <textarea
                    name="contract_scope"
                    value={contractFormData.contract_scope}
                    onChange={handleContractChange}
                    autoComplete="new-contract-scope"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Form Buttons */}
                <div className="col-span-3 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowContractEditForm(false);
                      setIsContractEditing(false);
                      setContractEditingId(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {t('contractDetail.updateContract')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default ContractDetail; 